import type { Duration } from '@app/_proto/google/protobuf/duration';
import { CHAPTER_EVENT_SOURCES } from '@app/analytics/constants';
import { ReactComponent as QuestionIcon } from '@app/assets/question.svg';
import QueryProgress from '@app/components/query.progress';
import { ChapterEvents } from '@app/domains/chapter/analytics/posthog/events';
import type { KarmaPaywallProps } from '@app/domains/chapter/components/ChapterKarmaPaywall';
import Paywall from '@app/domains/chapter/components/Paywall';
import Tooltip from '@app/domains/common/components/Tooltip';
import { pluralize } from '@app/domains/common/utils';
import { waitTimeSecToStr } from '@app/domains/novel/utils';
import { durationToNumber } from '@app/libs/utils';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useInterval } from 'react-use';

type Props = KarmaPaywallProps & {
    waitTime?: Duration;
    waitCompleteDate?: Date;
    maxCoupons?: number;
    remainingCoupons?: number;
    onUnlockByCoupon?: () => void;
};

export default function ChapterWaitToUnlockPaywall({
    waitTime,
    waitCompleteDate,
    karmaPrice,
    maxCoupons = 2,
    remainingCoupons,
    autoUnlockEnabled,
    canChapterUnlockByVoucher,
    chapterVoucherAmount,
    availableVouchers,
    isLoading,
    isKarmaLoading,
    isVoucherLoading,
    disabled,
    shouldEncourageVipFreeNovelSelect,
    shouldEncourageChampionSubscribe,
    onUnlockByKarma,
    onUnlockByVoucher,
    onUnlockByCoupon,
    onSubscribeChampion,
    onChangeAutoUnlock,
}: Props) {
    const waitTimeSec = durationToNumber(waitTime);
    const waitTimeReadableStr = waitTimeSecToStr({ waitTimeSec, isTimeUnitAbbreviated: false });

    const remainMs = (() => {
        if (!waitCompleteDate || !waitTime) return 0;
        const remain = dayjs(waitCompleteDate).diff(dayjs());
        return Math.max(remain, 0);
    })();

    const [remainTimeStr, setRemainTimeStr] = useState(() => formatDuration(remainMs));
    const [isTimeWaited, setIsTimeWaited] = useState(waitCompleteDate && waitTime && remainMs === 0);
    const delay = 1000;

    useInterval(
        () => {
            const durationLeft = getDurationFromNow(waitCompleteDate);
            setRemainTimeStr(formatDuration(durationLeft));
            if (durationLeft <= 0) {
                setIsTimeWaited(true);
            }
        },
        waitCompleteDate && waitTime && !isTimeWaited ? delay : null
    );

    const hasFreeOption = isTimeWaited || (waitCompleteDate && waitTime);

    return (
        <Paywall>
            <Paywall.TopIcon.Clock />
            <div className="flex items-center">
                <Paywall.Title title="Time Until Free Chapter" />
                <Tooltip
                    title={`If you wait ${waitTimeReadableStr} after reading an episode, you can read ${pluralize(
                        maxCoupons,
                        'chapter'
                    )} for free.`}
                >
                    <div className="ml-4">
                        <QuestionIcon className="h-18 w-18 text-gray-330 dark:text-gray-700" />
                    </div>
                </Tooltip>
            </div>
            {waitCompleteDate && waitTime && !isTimeWaited && <Paywall.Desc text={remainTimeStr} />}
            {isTimeWaited && (
                <Paywall.Desc
                    text={`${pluralize(remainingCoupons, 'Free Chapter')} Read Now`}
                    className="text-blue-600"
                />
            )}

            <QueryProgress status={isLoading ? 'loading' : 'idle'} />
            {!isLoading && (
                <>
                    {isTimeWaited && (
                        <div className="pt-12">
                            <Paywall.ReadFreeButton disabled={isLoading || disabled} onClick={onUnlockByCoupon} />
                        </div>
                    )}
                    {hasFreeOption && <Paywall.Or />}
                    {shouldEncourageVipFreeNovelSelect && <Paywall.VipFreeNovelEncourage />}
                    {canChapterUnlockByVoucher && (
                        <>
                            <Paywall.VoucherPayButton
                                isLoading={isVoucherLoading}
                                disabled={isLoading || disabled}
                                voucherAmount={chapterVoucherAmount}
                                availableVouchers={availableVouchers}
                                data-posthog-click-event={ChapterEvents.ClickByVoucher}
                                data-posthog-params={JSON.stringify({
                                    on: CHAPTER_EVENT_SOURCES.VoucherLock,
                                    'Voucher Amount': chapterVoucherAmount,
                                    'Auto-Unlock': autoUnlockEnabled,
                                })}
                                onClick={onUnlockByVoucher}
                            />
                            <Paywall.Or />
                        </>
                    )}
                    <Paywall.KarmaPayButton
                        isLoading={isKarmaLoading}
                        disabled={isLoading || disabled}
                        karmaPrice={karmaPrice}
                        data-posthog-click-event={ChapterEvents.ClickByKarma}
                        data-posthog-params={JSON.stringify({
                            on: CHAPTER_EVENT_SOURCES.WtuLock,
                            'Karma Count': karmaPrice,
                            'Auto-Unlock': autoUnlockEnabled,
                        })}
                        onClick={onUnlockByKarma}
                    />
                    {shouldEncourageChampionSubscribe && (
                        <Paywall.ChampionSubscribeButton
                            disabled={isLoading || disabled}
                            data-posthog-click-event={ChapterEvents.ClickByChampion}
                            data-posthog-params={JSON.stringify({
                                on: CHAPTER_EVENT_SOURCES.WtuLock,
                                'Auto-Unlock': autoUnlockEnabled,
                            })}
                            onClick={onSubscribeChampion}
                        />
                    )}
                    <div className="pt-14">
                        <Paywall.AutoUnlock defaultChecked={autoUnlockEnabled} onChange={onChangeAutoUnlock} />
                    </div>
                </>
            )}
        </Paywall>
    );
}

const getDurationFromNow = (date?: Date) => {
    if (!date) return 0;
    const now = dayjs();
    return dayjs(date).diff(now);
};

export const formatDuration = (durationIn: number) => {
    const duration = dayjs.duration(Math.max(durationIn, 0));
    if (duration.days() > 1) return duration.format('D [days] HH [h] : mm [m] : ss');
    if (duration.days() === 1) return duration.format('D [day] HH [h] : mm [m] : ss');
    return duration.format('HH [h] : mm [m] : ss');
};
